import { LoaderFunctionArgs, LoaderFunction } from 'react-router-dom'
import reverse from 'lodash/reverse'

import { eventsApi } from '../../services/events'
import type { AppStore } from '../../store'
import {
  ParticipantStatus,
  Options,
  Events,
  ParticipantOptionStatus,
  AbstractSelectionStatus,
} from '../../types'

export const adminEventsLoader = (store: AppStore) =>
  (async () => {
    const { data } = await store.dispatch(
      eventsApi.endpoints.listEvents.initiate(undefined, { forceRefetch: true })
    )

    return reverse(data ? [...data] : [])
  }) satisfies LoaderFunction

export const adminEventDetailsLoader = (store: AppStore) =>
  (async ({ params }: LoaderFunctionArgs) => {
    const { eventId } = params
    const skip = !eventId || Number.isNaN(Number(eventId))
    if (skip) return { event: undefined, subEvents: undefined }

    const { data: event } = await store.dispatch(
      eventsApi.endpoints.getEvent.initiate(Number(eventId), { forceRefetch: true })
    )

    const { data: subEvents } = await store.dispatch(
      eventsApi.endpoints.listSubEvents.initiate({ id: Number(eventId) }, { forceRefetch: true })
    )
    const { data: sessions } = await store.dispatch(
      eventsApi.endpoints.listSessions.initiate(Number(eventId), { forceRefetch: true })
    )
    const { data: options } = await store.dispatch(
      eventsApi.endpoints.listOptions.initiate(Number(eventId), { forceRefetch: true })
    )

    return {
      event: event as Events<true>,
      subEvents,
      sessions,
      options: options as Options<true>[],
    }
  }) satisfies LoaderFunction

export const adminOptionsLoader = (store: AppStore) =>
  (async ({ params }: LoaderFunctionArgs) => {
    const { eventId } = params
    // const pageSize = new URL(request.url).searchParams.get('pageSize')
    // const page = new URL(request.url).searchParams.get('page')

    const skip = !eventId || Number.isNaN(Number(eventId))
    if (skip) return undefined

    const { data } = await store.dispatch(
      eventsApi.endpoints.listOptions.initiate(Number(eventId), { forceRefetch: true })
    )

    return data as Options<true>[] | undefined
  }) satisfies LoaderFunction

export const adminAbstractsLoader = (store: AppStore) =>
  (async ({ params, request }: LoaderFunctionArgs) => {
    const { eventId } = params
    const pageSize = new URL(request.url).searchParams.get('pageSize')
    const page = new URL(request.url).searchParams.get('page')

    const skip = !eventId || Number.isNaN(Number(eventId))
    if (skip)
      return {
        abstracts: undefined,
        statistics: undefined,
      }

    const { data: abstracts } = await store.dispatch(
      eventsApi.endpoints.listAbstractsSelection.initiate(
        {
          eventId: Number(eventId),
          pageSize: pageSize ? Number(pageSize) : undefined,
          page: page ? Number(page) : undefined,
        },
        { forceRefetch: true }
      )
    )
    const { data: statistics } = await store.dispatch(
      eventsApi.endpoints.getAbstractsStatistics.initiate(Number(eventId), { forceRefetch: true })
    )

    return { abstracts, statistics }
  }) satisfies LoaderFunction

const SelectionStatusFilter = [
  AbstractSelectionStatus.DisplayPoster,
  AbstractSelectionStatus.InteractivePoster,
  AbstractSelectionStatus.OralPresentation,
  AbstractSelectionStatus.ThematicSession,
]

export const adminSessionAbstractsLoader = (store: AppStore) =>
  (async ({ params, request }: LoaderFunctionArgs) => {
    const { eventId } = params
    const pageSize = new URL(request.url).searchParams.get('pageSize')
    const page = new URL(request.url).searchParams.get('page')

    const skip = !eventId || Number.isNaN(Number(eventId))
    if (skip)
      return {
        abstracts: [],
        statistics: undefined,
      }

    const { data: abstracts } = await store.dispatch(
      eventsApi.endpoints.listAbstractsSelection.initiate(
        {
          eventId: Number(eventId),
          pageSize: pageSize ? Number(pageSize) : undefined,
          page: page ? Number(page) : undefined,
        },
        { forceRefetch: true }
      )
    )
    const { data: statistics } = await store.dispatch(
      eventsApi.endpoints.getAbstractsStatistics.initiate(Number(eventId), { forceRefetch: true })
    )

    return {
      abstracts:
        abstracts?.results.filter(abstract =>
          SelectionStatusFilter.includes(abstract.selection_status)
        ) ?? [],
      statistics,
    }
  }) satisfies LoaderFunction

export const adminParticipantsLoader = (store: AppStore) =>
  (async ({ params, request }: LoaderFunctionArgs) => {
    const { eventId } = params
    const pageSize = new URL(request.url).searchParams.get('pageSize')
    const page = new URL(request.url).searchParams.get('page')
    const status = new URL(request.url).searchParams.get('status') as ParticipantStatus

    const skip = !eventId || Number.isNaN(Number(eventId))
    if (skip) return { participants: undefined }

    const { data: participants } = await store.dispatch(
      eventsApi.endpoints.listParticipants.initiate(
        {
          eventId: Number(eventId),
          pageSize: pageSize ? Number(pageSize) : undefined,
          page: page ? Number(page) : undefined,
          status,
        },
        { forceRefetch: true }
      )
    )

    return { participants }
  }) satisfies LoaderFunction

export const adminOptionsParticipantsLoader = (store: AppStore) =>
  (async ({ params, request }: LoaderFunctionArgs) => {
    const { eventId, optionId } = params
    const pageSize = new URL(request.url).searchParams.get('pageSize')
    const page = new URL(request.url).searchParams.get('page')
    const status = new URL(request.url).searchParams.get('status') as ParticipantOptionStatus

    const skip = !eventId || Number.isNaN(Number(eventId))
    if (skip) return { participants: undefined }

    const { data: participants } = await store.dispatch(
      eventsApi.endpoints.listOptionsParticipants.initiate(
        {
          eventId: Number(eventId),
          optionId: Number.isNaN(Number(optionId)) ? undefined : Number(optionId),
          pageSize: pageSize ? Number(pageSize) : undefined,
          page: page ? Number(page) : undefined,
          status,
        },
        { forceRefetch: true }
      )
    )

    return { participants }
  }) satisfies LoaderFunction

export const adminParticipantDetailsLoader = (store: AppStore) =>
  (async ({ params }: LoaderFunctionArgs) => {
    const { eventId, participantId } = params
    const skip =
      !eventId ||
      !participantId ||
      Number.isNaN(Number(eventId)) ||
      Number.isNaN(Number(participantId))
    if (skip) return undefined

    const { data } = await store.dispatch(
      eventsApi.endpoints.getParticipant.initiate(
        {
          eventId: Number(eventId),
          id: Number(participantId),
        },
        { forceRefetch: true }
      )
    )

    return data
  }) satisfies LoaderFunction

export const adminHotelOverviewLoader = (store: AppStore) =>
  (async ({ params }: LoaderFunctionArgs) => {
    const { eventId } = params
    const skip = !eventId || Number.isNaN(Number(eventId))
    if (skip) return undefined

    const { data } = await store.dispatch(
      eventsApi.endpoints.getHotelOverview.initiate(Number(eventId), { forceRefetch: true })
    )

    return data
  }) satisfies LoaderFunction

export const adminMealOverviewLoader = (store: AppStore) =>
  (async ({ params }: LoaderFunctionArgs) => {
    const { eventId } = params
    const skip = !eventId || Number.isNaN(Number(eventId))
    if (skip) return undefined

    const { data } = await store.dispatch(
      eventsApi.endpoints.getMealOverview.initiate(Number(eventId), { forceRefetch: true })
    )

    return data
  }) satisfies LoaderFunction

export const adminContributorOverviewLoader = (store: AppStore) =>
  (async ({ params }: LoaderFunctionArgs) => {
    const { eventId } = params
    const skip = !eventId || Number.isNaN(Number(eventId))
    if (skip) return undefined

    const { data } = await store.dispatch(
      eventsApi.endpoints.getContributorOverview.initiate(Number(eventId), { forceRefetch: true })
    )

    return data
  }) satisfies LoaderFunction

export const adminSponsorshipLoader = (store: AppStore) =>
  (async ({ params }: LoaderFunctionArgs) => {
    const { eventId } = params
    const skip = !eventId || Number.isNaN(Number(eventId))
    if (skip) return { sponsorship: undefined }

    const { data: sponsorship } = await store.dispatch(
      eventsApi.endpoints.listSponsorships.initiate(Number(eventId), { forceRefetch: true })
    )

    const { data: sponsorshipStats } = await store.dispatch(
      eventsApi.endpoints.sponsorshipStats.initiate(Number(eventId), { forceRefetch: true })
    )

    return { sponsorship, sponsorshipStats }
  }) satisfies LoaderFunction

export const adminSponsorshipByIdLoader = (store: AppStore) =>
  (async ({ params }: LoaderFunctionArgs) => {
    const { eventId, sponsorshipId } = params
    const skip = !eventId || Number.isNaN(Number(eventId))
    if (skip) return { sponsorship: undefined }

    const { data: sponsorship } = await store.dispatch(
      eventsApi.endpoints.getSponsorship.initiate(
        { eventId: Number(eventId), id: Number(sponsorshipId) },
        { forceRefetch: true }
      )
    )

    return { sponsorship }
  }) satisfies LoaderFunction
